import React from 'react'
import { Tooltip } from 'antd'
import { PercentageOutlined } from '@ant-design/icons'
import { FiMousePointer, FiCheckCircle } from 'react-icons/fi'
import { KPICardMini } from '@pushly/aqe/lib/components/kpi-card/kpi-card-mini'
import './stats-row.scss'

interface StatsRowProps {
    impressions: { display: string; full?: string; isCompact?: boolean }
    clicks: { display: string; full?: string; isCompact?: boolean }
    ctr: string
    /** When true, shows loading skeleton KPI cards */
    loading?: boolean
    /** Active metric key (for selectable mode). Omit for display-only mode. */
    activeMetric?: 'impressions' | 'clicks' | 'ctr'
    /** Called when a metric card is tapped (selectable mode only) */
    onMetricTap?: (metric: 'impressions' | 'clicks' | 'ctr') => void
}

/**
 * Shared 3-column KPI stats row (Impressions, Clicks, CTR).
 * Supports two modes:
 * - Display-only (notification detail): shows values with optional tooltip for compact numbers
 * - Selectable (insights): cards are tappable with active ring indicator
 */
export const StatsRow: React.FC<StatsRowProps> = ({ impressions, clicks, ctr, loading, activeMetric, onMetricTap }) => {
    const isSelectable = !!onMetricTap

    if (loading) {
        return (
            <div className="stats-row">
                <KPICardMini label="Impressions" formattedValue="" loading />
                <KPICardMini label="Clicks" formattedValue="" loading />
                <KPICardMini label="CTR" formattedValue="" loading />
            </div>
        )
    }

    const renderCard = (
        metric: 'impressions' | 'clicks' | 'ctr',
        label: string,
        value: string,
        icon: React.ReactNode,
        tileColor: 'slate' | 'purple' | 'teal',
        tooltipTitle?: string,
    ) => {
        const card = <KPICardMini label={label} formattedValue={value} icon={icon} tileColor={tileColor} />

        if (isSelectable) {
            return (
                <div
                    key={metric}
                    className={`stats-row__item ${activeMetric === metric ? 'stats-row__item--active' : ''}`}
                    onClick={() => onMetricTap(metric)}
                    role="button"
                    tabIndex={0}
                    aria-pressed={activeMetric === metric}
                    aria-label={`Show ${label.toLowerCase()}`}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onMetricTap(metric)
                        }
                    }}
                >
                    {card}
                </div>
            )
        }

        if (tooltipTitle) {
            return (
                <Tooltip key={metric} title={tooltipTitle} trigger="click">
                    <div>{card}</div>
                </Tooltip>
            )
        }

        return <React.Fragment key={metric}>{card}</React.Fragment>
    }

    return (
        <div className="stats-row">
            {renderCard(
                'impressions',
                'Impressions',
                impressions.display,
                <FiCheckCircle />,
                'slate',
                impressions.isCompact ? impressions.full : undefined,
            )}
            {renderCard(
                'clicks',
                'Clicks',
                clicks.display,
                <FiMousePointer />,
                'purple',
                clicks.isCompact ? clicks.full : undefined,
            )}
            {renderCard('ctr', 'CTR', ctr, <PercentageOutlined />, 'teal')}
        </div>
    )
}
